import { useEffect, useRef } from "react";
import { nextPollDelayMs } from "@/shared/lib/pollingPolicy";
import { getHttpStatus } from "@/shared/lib/httpStatus";

/**
 * Session-aware polling: fetch now, then re-fetch on the cadence decided by
 * `nextPollDelayMs` (30 s visible, 5 min hidden, backoff on 5xx, stop on
 * 401/403). Returning to the tab fetches at once. `fetch` must reject on
 * failure so the status can be inspected. A change of `key` (e.g. the
 * campaign being viewed) restarts the cycle with an immediate fetch.
 */
export function usePolling(
	fetch: () => Promise<unknown>,
	options: { enabled: boolean; key?: string | number | null },
): void {
	const { enabled, key = null } = options;
	const fetchRef = useRef(fetch);
	fetchRef.current = fetch;

	useEffect(() => {
		if (!enabled) return;

		let cancelled = false;
		let stopped = false;
		let inFlight = false;
		let timer: number | null = null;
		let consecutiveErrors = 0;
		let lastStatus: number | null | undefined;

		const clearTimer = () => {
			if (timer !== null) {
				window.clearTimeout(timer);
				timer = null;
			}
		};

		const schedule = () => {
			clearTimer();
			if (cancelled || stopped) return;
			const delay = nextPollDelayMs({
				visible: document.visibilityState !== "hidden",
				lastStatus,
				consecutiveErrors,
			});
			if (delay === null) {
				stopped = true;
				return;
			}
			timer = window.setTimeout(() => void run(), delay);
		};

		const run = async () => {
			if (cancelled || stopped || inFlight) return;
			inFlight = true;
			try {
				await fetchRef.current();
				lastStatus = 200;
				consecutiveErrors = 0;
			} catch (error) {
				lastStatus = getHttpStatus(error);
				consecutiveErrors += 1;
			} finally {
				inFlight = false;
			}
			schedule();
		};

		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				schedule(); // switch to the hidden cadence without waiting for the current timer
			} else {
				clearTimer();
				void run();
			}
		};
		const onFocus = () => {
			clearTimer();
			void run();
		};

		document.addEventListener("visibilitychange", onVisibilityChange);
		window.addEventListener("focus", onFocus);
		void run();

		return () => {
			cancelled = true;
			clearTimer();
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.removeEventListener("focus", onFocus);
		};
	}, [enabled, key]);
}
